import { useEffect, useRef, useState } from "react";

/**
 * Attach to any element to trigger reveal-on-scroll.
 * Adds `is-visible` when the element enters the viewport.
 * Respects prefers-reduced-motion via the CSS layer.
 */
export function useReveal<T extends HTMLElement>(threshold = 0.15) {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setSeen(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, seen]);
  return { ref, className: seen ? "reveal is-visible" : "reveal" };
}
